/**
 * StreamyMusic PWA entry point (TASK 7.1 -> 7.3).
 *
 * `index.html` loads exactly this module (`<script type="module"
 * src="/src/main.ts">`), and this module is the ONLY thing that pulls in the
 * stylesheet graph — `./modules/styles/index.css` fans out to tokens, fonts,
 * base, layout, components and motion.
 *
 * Sequence:
 *   1. paint the boot screen (mock L31-36) so the first frame is never blank;
 *   2. hand over to `modules/app/index.js#startApp`, which mounts the shell
 *      (header + main + nav), starts the hash router, and — in DEV only —
 *      seeds the store from the mock-parity fixture.
 *
 * Everything TASK 7.2/7.3 builds is reachable from here; nothing in
 * `modules/app/` exists solely for a test.
 */
import './modules/styles/index.css';

import { createUpdateReloadController } from './lib/update-reload.js';
import { startApp } from './modules/app/index.js';
import { getState } from './modules/app/state-store.js';
import { mountBootScreen } from './modules/boot.js';

const root = document.getElementById('app');

mountBootScreen(root);

void startApp({ root }).catch((error: unknown) => {
  // A boot failure must not leave the splash spinning forever with no
  // explanation in the console.
  console.error('StreamyMusic failed to start', error);
});

/*
 * Service worker (TASK 10.1). Registered AFTER `startApp` is kicked off so the
 * registration never sits in front of first paint, and only in a PRODUCTION
 * build: the dev server has no `/sw.js` (the worker is emitted by the
 * `streamymusic-service-worker` plugin in vite.config.ts, which runs on build
 * only), and a worker that cached Vite's dev module graph would serve stale
 * modules for the rest of the session.
 *
 * `scope: '/'` is explicit: the worker must see `/api/stream/*` and
 * `/api/art/*`, which are siblings of the script rather than children of it.
 * A failed registration is logged and otherwise ignored — the app is fully
 * functional online without a service worker, and Safari private browsing
 * refuses registration outright.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((error: unknown) => {
    console.warn('StreamyMusic: service worker registration failed', error);
  });

  /*
   * TASK 11.5 — the page's half of the PWA update flow. The deploy AC ("a new
   * deploy activates on clients within 24 h with NO user action, and a broken
   * cached build is never sticky") needs the foreground to NUDGE the worker to
   * check for a newer /sw.js, and to reload once a new build takes control.
   *
   * The two message strings are the stable contract with `sw/service-worker.ts`
   * (`SW_MESSAGE_CHECK_UPDATE` / `SW_MESSAGE_UPDATED`) — inlined rather than
   * imported so the worker module is not pulled into the app entry bundle (it
   * ships as its own worker bundle). The worker THROTTLES the real
   * `registration.update()`, so pinging on every foreground is safe.
   */
  const SW_CHECK_UPDATE = 'streamymusic:check-update';
  const SW_UPDATED = 'streamymusic:sw-updated';

  const pingForUpdate = (): void => {
    navigator.serviceWorker.controller?.postMessage({ type: SW_CHECK_UPDATE });
  };

  /*
   * Whether a worker ALREADY controlled this page at load. Captured up front,
   * before the message listener can fire: `controller` is null on a first
   * install (nothing to reload into) and non-null on a return visit, which is
   * exactly what tells a genuine update apart from a spurious first-install one.
   */
  const hadControllerAtLoad = navigator.serviceWorker.controller !== null;

  /*
   * The SW_MESSAGE_UPDATED signal is advisory (see sw/service-worker.ts): the
   * page must reload only when it won't disrupt the user — never on first
   * install, never mid-playback, never while the tab is visible. The controller
   * arms a pending reload and applies it on the next visibilitychange to hidden
   * while playback is idle (with a reload-loop guard).
   */
  const updateReload = createUpdateReloadController({
    hadControllerAtLoad,
    isPlaying: () => getState().playback.playing,
    isVisible: () => document.visibilityState === 'visible',
    reload: () => {
      window.location.reload();
    },
    onVisibilityChange: (handler) => {
      document.addEventListener('visibilitychange', handler);
    },
  });

  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: unknown } | null;
    if (data !== null && typeof data === 'object' && data.type === SW_UPDATED) {
      updateReload.onUpdateMessage();
    }
  });

  // On load, and whenever the tab returns to the foreground.
  pingForUpdate();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') pingForUpdate();
  });
}
